/*
 * @providesModule Label
 *
 */

import React from 'react';
import {Text, View, TextStyle, StyleSheet, TextProps} from 'react-native';

function isTruncated(node) {
  return node.offsetWidth < node.scrollWidth;
}

const px = (size) => `${size}px`;

type LabelProps = {
  fontSize?: number;
  minFontSize: number;
  children: TextProps['children'];
  numberOfLines?: number;
  adjustsFontSizeToFit?: boolean;
  minimumFontScale?: number;
  allowFontScaling?: boolean;
  style: TextStyle;
};

const containerStyles = new Set([
  'flex',
  'margin',
  'marginRight',
  'marginLeft',
  'marginHorizontal',
  'marginVertical',
  'marginTop',
  'marginBottom',
]);

export default class Label extends React.PureComponent<LabelProps> {
  containerRef;

  constructor(props) {
    super(props);

    this.containerRef = React.createRef();
  }

  static defaultProps = {
    minFontSize: 2,
  };

  componentDidMount() {
    this._shrink();
  }

  componentDidUpdate() {
    this._shrink();
  }

  _setFontSize(fontSize: number) {
    const containerRef = this.containerRef.current;
    containerRef.style.fontSize = px(fontSize);
    return isTruncated(containerRef);
  }

  _shrink() {
    const {minFontSize} = this.props;
    const containerRef = this.containerRef.current;

    containerRef.style.fontSize = null;
    const fontSize = this.props.fontSize || parseInt(getComputedStyle(containerRef).fontSize, 10);

    let max = fontSize;
    let min = minFontSize;
    let truncated = false;
    let found = min;

    while (max > min && min < max) {
      // test new font size
      const newFontSize = Math.floor((max + min) / 2);
      truncated = this._setFontSize(newFontSize);

      if (truncated) {
        // decrease max
        max = newFontSize - 1;
      } else {
        // Record new max found font size
        if (newFontSize > found) found = newFontSize;

        // increase min
        min = newFontSize + 1;
      }
    }

    this._setFontSize(found);
  }

  getStyles() {
    const styles = StyleSheet.flatten(this.props.style);
    return Object.entries(styles).reduce(
      (acc, [styleName, styleValue]) => {
        if (containerStyles.has(styleName)) {
          acc.containerStyles[styleName] = styleValue;
        } else {
          acc.textStyles[styleName] = styleValue;
        }
        return acc;
      },
      {containerStyles: {}, textStyles: {}}
    );
  }

  render() {
    const {containerStyles, textStyles} = this.getStyles();
    const {children, ...textProps} = this.props;
    return (
      <View ref={this.containerRef} style={containerStyles}>
        <Text
          {...textProps}
          style={textStyles}
          numberOfLines={this.props.numberOfLines === 0 ? 0 : (this.props.numberOfLines ?? 1)}
          adjustsFontSizeToFit={this.props.adjustsFontSizeToFit ?? true}
          minimumFontScale={this.props.minimumFontScale ?? 0.1}
          allowFontScaling={false}
        >
          {children}
        </Text>
      </View>
    );
  }
}
